import { useEffect, useRef, useState } from "react"
import { fetchQuery, graphql } from 'relay-runtime'
import Env from '@src/RelayEnvironment'
import type { Game } from '@src/components/Game/Game.types'
import { ThumbnailSliderSimilarGamesQuery } from "./__generated__/ThumbnailSliderSimilarGamesQuery.graphql"

export const useSimilarGames = (shouldFetch: boolean, gameCode: string, isMobile: boolean, limit: number = 10) => {
  const [games, setGames] = useState<Game[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const hasFetchedRef = useRef(false)
  const previousGameCodeRef = useRef<string>(gameCode)

  useEffect(() => {
    // Reset the hasFetched flag when gameCode changes
    if (previousGameCodeRef.current !== gameCode) {
      hasFetchedRef.current = false
      previousGameCodeRef.current = gameCode
    }

    if (!shouldFetch || hasFetchedRef.current) {
        setIsLoading(false)
        return
    }

    setIsLoading(true)

    fetchQuery<ThumbnailSliderSimilarGamesQuery>(
      Env,
      similarGamesQuery,
      {
        gameCode,
        mobile: isMobile,
        limit
      },
      { fetchPolicy: 'network-only' }
    ).subscribe({
      next: (data) => {
        setGames([...data.listSimilarGames])
        setIsLoading(false)
        hasFetchedRef.current = true
      },
      error: (err) => {
        console.error('Error fetching similar games:', err)
        setIsLoading(false)
      },
      complete: () => {
        setIsLoading(false)
      },
    })
  }, [shouldFetch, gameCode, isMobile, limit])

  return { games, isLoading }
}

const similarGamesQuery = graphql`
  query ThumbnailSliderSimilarGamesQuery($gameCode: String!, $mobile: Boolean, $limit: Int) {
    listSimilarGames(gameCode: $gameCode, mobile: $mobile, limit: $limit) {
      urlThumb
      aggregator
      gameId
      category
      provider
      label
      customThumb
    }
  }
` 